import { ReportBuilderDataSourceId } from '@principle-theorem/principle-core/interfaces';
import { FactTables } from '../../models/fact-tables';
import { toMeasureBuilderData } from '../../models/measure-properties';
import { ReportBuilderColumns } from '../../report-builder-columns';
import { ChartFilterDefinition } from '../charts/chart-filter-definition';
import { ReportBuilderCharts } from '../charts/report-builder-charts';
import { ReportBuilderCommonCharts } from '../charts/report-builder-common-charts';
import { ReportBuilderDataSourceInstance } from '../report-builder-data-source-instance';
import { ReportBuilderGroups } from '../report-builder-groups';
import { ReportBuilderScopeDataAccessors } from '../report-builder-scope-data-accessors';

export class PractitionerIncomeDataSource extends ReportBuilderDataSourceInstance {
  override readonly id = ReportBuilderDataSourceId.PractitionerIncome;
  override readonly name = 'Practitioner Income';
  override readonly description = `One result per practitioner for each transaction within Principle. This details the practitioner's allocated portion of each transaction. This includes information about the practice, patient, and invoice associated with each result.`;
  override readonly factTable = FactTables.transactionEvent.latestEvent;
  override readonly isRetired = true;
  override readonly rowItemName = 'Record';

  override readonly queryableTimestamps = [
    { property: this.factTable.transaction.createdAt },
    { property: this.factTable.invoice.createdAt },
    { property: this.factTable.invoice.issuedAt },
    { property: this.factTable.invoice.paidAt },
  ];

  scopes = [];
  scopeDataAccessor = ReportBuilderScopeDataAccessors.build([]);

  override readonly groupByOptions = [
    ReportBuilderGroups.toGroup('Common', [
      ReportBuilderGroups.toOption(this.factTable.transaction.createdAt),
      ReportBuilderGroups.toOption(this.factTable.practitionerName),
      ReportBuilderGroups.toOption(this.factTable.practice.name),
      ReportBuilderGroups.toOption(this.factTable.brand.name),
      ReportBuilderGroups.toOption(this.factTable.count),
    ]),
    ReportBuilderGroups.transaction(this.factTable.transaction),
    ReportBuilderGroups.invoice(this.factTable.invoice),
    ReportBuilderGroups.patient(this.factTable.patient),
  ];

  override readonly availableColumns = [
    ReportBuilderColumns.toColumnGroup('Common', [
      this.factTable.practitionerName,
      this.factTable.practitionerPayments,
    ]),
    ReportBuilderColumns.toColumnGroup('Invoice', [
      ...ReportBuilderColumns.invoice(this.factTable.invoice),
      this.factTable.invoiceLink,
    ]),
    ReportBuilderColumns.toColumnGroup('Patient', [
      ...ReportBuilderColumns.patient(this.factTable.patient),
      this.factTable.patientLink,
    ]),
    ReportBuilderColumns.toColumnGroup(
      'Transaction',
      ReportBuilderColumns.transaction(this.factTable.transaction)
    ),
  ];

  override readonly defaultColumns = ReportBuilderColumns.toReferences([
    this.factTable.patient.name,
    this.factTable.invoice.status,
    this.factTable.practitionerName,
    this.factTable.practitionerPayments,
    this.factTable.transaction.createdAt,
    this.factTable.transaction.status,
    this.factTable.transaction.type,
    this.factTable.transaction.provider,
  ]);

  override readonly availableCharts = [
    ReportBuilderCharts.predefined(
      'overview.totalPayments',
      this.factTable.transaction.createdAt,
      ChartFilterDefinition.columnOverTime(
        'Total Payments',
        this.factTable.transaction.createdAt,
        [toMeasureBuilderData(this.factTable.practitionerPayments)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practice',
      this.factTable.practice.name,
      ChartFilterDefinition.pie('Practice', this.factTable.practice.name, [
        toMeasureBuilderData(this.factTable.practitionerPayments),
      ])
    ),
    ...ReportBuilderCommonCharts.patient(
      this.factTable.patient,
      this.factTable.practitionerPayments
    ),
    ...ReportBuilderCommonCharts.invoice(
      this.factTable.invoice,
      this.factTable.practitionerPayments
    ),
    ReportBuilderCharts.predefined(
      'transaction.type',
      this.factTable.transaction.type,
      ChartFilterDefinition.row('Type', this.factTable.transaction.type, [
        toMeasureBuilderData(this.factTable.practitionerPayments),
      ])
    ),
    ReportBuilderCharts.predefined(
      'transaction.status',
      this.factTable.transaction.status,
      ChartFilterDefinition.row('Status', this.factTable.transaction.status, [
        toMeasureBuilderData(this.factTable.practitionerPayments),
      ])
    ),
    ReportBuilderCharts.predefined(
      'transaction.provider',
      this.factTable.transaction.provider,
      ChartFilterDefinition.column(
        'Provider',
        this.factTable.transaction.provider,
        [toMeasureBuilderData(this.factTable.practitionerPayments)],
        { rotateXAxisLabels: true }
      )
    ),
    ReportBuilderCharts.predefined(
      'transaction.providerSubType',
      this.factTable.transaction.providerSubType,
      ChartFilterDefinition.row(
        'Provider SubType',
        this.factTable.transaction.providerSubType,
        [toMeasureBuilderData(this.factTable.practitionerPayments)]
      )
    ),
    ReportBuilderCharts.predefined(
      'transaction.amount',
      this.factTable.transaction.createdAt,
      ChartFilterDefinition.columnOverTime(
        'Amount',
        this.factTable.transaction.createdAt,
        [toMeasureBuilderData(this.factTable.transaction.amount)]
      )
    ),
    ReportBuilderCharts.predefined(
      'overview.practitioner',
      this.factTable.practitionerName,
      ChartFilterDefinition.row(
        'Practitioner',
        this.factTable.practitionerName,
        [toMeasureBuilderData(this.factTable.practitionerPayments)]
      )
    ),
  ];
}
